import React, { useState, useEffect } from 'react';

// Define common icons as React components (similar to lucide-react)
const Zap = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>;
const Search = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>;
const FileText = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>;
const Cloud = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M17.5 19H9a7 7 0 1 1 6.71-9h-.71A4.5 4.5 0 0 0 17.5 19Z"></path></svg>;
const ShieldCheck = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></svg>;
const Sliders = (props) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><line x1="4" x2="4" y1="21" y2="14"></line><line x1="4" x2="4" y1="10" y2="3"></line><line x1="12" x2="12" y1="21" y2="12"></line><line x1="12" x2="12" y1="8" y2="3"></line><line x1="20" x2="20" y1="21" y2="16"></line><line x1="20" x2="20" y1="12" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg>;


// 3CS logo colors (approximated from the image)
const C3S_COLOR_DARK_BLUE = '#1a5585';
const C3S_COLOR_LIGHT_BLUE = '#3e8fb0';

// Metric thresholds (Google PageSpeed Insights v5, in ms for time-based metrics, unitless for CLS)
const METRIC_THRESHOLDS = {
    FCP: { good: 1800, average: 3000 }, // ms (0-1.8s Good, 1.8-3s Needs Improvement, >3s Poor)
    LCP: { good: 2500, average: 4000 }, // ms (0-2.5s Good, 2.5-4ss Needs Improvement, >4s Poor)
    TBT: { good: 200, average: 600 },    // ms (0-200ms Good, 200-600ms Needs Improvement, >600ms Poor)
    CLS: { good: 0.1, average: 0.25 },   // (0-0.1 Good, 0.1-0.25 Needs Improvement, >0.25 Poor)
    SpeedIndex: { good: 3400, average: 5800 }, // ms (0-3.4s Good, 3.4-5.8s Needs Improvement, >5.8s Poor)
};

// Helper to parse metric strings into numbers (in ms for time-based, float for CLS)
const parseMetricValue = (valueString, metricKey) => {
    if (typeof valueString === 'number') return valueString;
    if (typeof valueString !== 'string') return NaN;

    const lowerCaseValue = valueString.toLowerCase().trim();
    const match = lowerCaseValue.match(/^(\d+(\.\d+)?)\s*(ms|s)?$/);

    if (!match) {
        return NaN;
    }

    const num = parseFloat(match[1]);
    const unit = match[3];

    if (isNaN(num)) {
        return NaN;
    }

    if (metricKey.toUpperCase() === 'CLS') {
        return num;
    }

    if (unit === 's') {
        return num * 1000;
    }
    return num;
};

// Helper to determine metric status (Good, Average, Poor)
const getMetricStatus = (metricId, value) => {
    const parsedValue = parseMetricValue(value, metricId);
    if (isNaN(parsedValue)) return 'N/A';

    const thresholds = METRIC_THRESHOLDS[metricId.toUpperCase()];
    if (!thresholds) return 'N/A';

    if (metricId.toUpperCase() === 'CLS') {
        if (parsedValue <= thresholds.good) return 'Good';
        if (parsedValue <= thresholds.average) return 'Average';
        return 'Poor';
    } else {
        if (parsedValue <= thresholds.good) return 'Good';
        if (parsedValue <= thresholds.average) return 'Average';
        return 'Poor';
    }
};

// Helper for status color classes for card backgrounds and text
const getStatusClasses = (currentStatus, theme) => {
    if (theme === 'dark') {
        switch (currentStatus) {
            case 'Good': return 'bg-green-900 text-green-100 border-green-700';
            case 'Poor': return 'bg-red-900 text-red-100 border-red-700';
            case 'Average': return 'bg-yellow-900 text-yellow-100 border-yellow-700';
            case 'N/A': return 'bg-gray-700 text-gray-200 border-gray-600';
            default: return 'bg-gray-700 text-gray-200 border-gray-600';
        }
    } else {
        switch (currentStatus) {
            case 'Good': return 'bg-green-100 text-green-800 border-green-300';
            case 'Poor': return 'bg-red-100 text-red-800 border-red-300';
            case 'Average': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'N/A': return 'bg-gray-100 text-gray-800 border-gray-300';
            default: return 'bg-blue-50 text-blue-800 border-blue-200';
        }
    }
};


// Component for displaying performance metrics as tiles
const PerformanceDisplayTile = ({ metricName, value, theme }) => {
    const status = typeof value === 'string' ? getMetricStatus(metricName, value) : 'N/A';
    const displayValue = value;

    const cardClasses = getStatusClasses(status, theme);

    return (
        <div className={`flex flex-col items-center justify-center p-4 rounded-lg shadow-sm border ${cardClasses}`}>
            <h3 className={`text-lg font-semibold ${cardClasses.includes('text-') ? '' : (theme === 'dark' ? 'text-gray-100' : 'text-gray-800')} mb-2 text-center`}>{metricName.toUpperCase()}</h3>
            <p className={`text-2xl font-bold ${cardClasses.includes('text-') ? '' : (theme === 'dark' ? 'text-gray-50' : 'text-gray-900')}`}>{displayValue}</p>
            <div className={`text-sm font-medium mt-2 px-2 py-1 rounded-full ${getStatusClasses(status, theme)}`}>
                {status}
            </div>
        </div>
    );
};


// AuditItem component for displaying individual audit points (SEO & Technical)
const AuditItem = ({ item, automatedValue = null, theme }) => {
    const displayStatus = automatedValue !== null ? (
        typeof automatedValue === 'boolean'
            ? (automatedValue ? 'Good' : 'Poor')
            : (item.metricKey && typeof automatedValue === 'string'
                ? getMetricStatus(item.metricKey, automatedValue)
                : (typeof automatedValue === 'string' && (automatedValue === 'Good' || automatedValue === 'Average' || automatedValue === 'Poor')
                    ? automatedValue
                    : 'N/A')
            )
    ) : 'N/A';

    const cardClasses = getStatusClasses(displayStatus, theme);

    return (
        <div className={`p-4 mb-4 rounded-lg shadow-sm transition-all duration-200 ease-in-out border ${cardClasses}`}>
            <div className="flex items-center">
                <span className={`font-semibold flex-grow ${cardClasses.includes('text-') ? '' : (theme === 'dark' ? 'text-gray-100' : 'text-gray-800')}`}>
                    {item.question}
                </span>
                {automatedValue !== null && (
                    <span className={`text-sm px-2 py-1 rounded-full font-medium ${getStatusClasses(displayStatus, theme)}`}>
                        {typeof automatedValue === 'boolean' ? (automatedValue ? 'Yes' : 'No') : (item.metricKey ? `${automatedValue}` : automatedValue)}
                    </span>
                )}
            </div>
        </div>
    );
};


// AuditSection component to group related audit items
const AuditSection = ({ section, automatedResults, theme }) => {
    const IconComponent = section.icon;

    return (
        <div className={`p-6 rounded-xl shadow-lg mb-8 border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h2 className={`text-2xl font-bold mb-6 flex items-center ${theme === 'dark' ? 'text-blue-400' : C3S_COLOR_DARK_BLUE}`}>
                {IconComponent && <IconComponent className={`w-7 h-7 mr-3 ${theme === 'dark' ? 'text-blue-400' : C3S_COLOR_LIGHT_BLUE}`} />}
                {section.title}
            </h2>

            {(section.id === 'performance_desktop' || section.id === 'performance_mobile') && automatedResults && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {section.items.map(item => (
                        <PerformanceDisplayTile
                            key={item.id}
                            metricName={item.metricKey}
                            value={automatedResults?.performance?.[section.id.replace('performance_', '')]?.[item.metricKey] || 'N/A'}
                            theme={theme}
                        />
                    ))}
                </div>
            )}

            {!(section.id === 'performance_desktop' || section.id === 'performance_mobile') && section.items.map((item) => {
                let automatedValue = null;
                if (automatedResults) {
                    if (section.id === 'seo') {
                        if (item.id === 'meta_description') automatedValue = automatedResults.metaDescription !== 'Not found';
                        if (item.id === 'title_tag_presence') automatedValue = automatedResults.pageTitle !== 'Not found' && automatedResults.pageTitle !== '';
                        if (item.id === 'crawlable_links') automatedValue = automatedResults.hasLinks;
                        if (item.id === 'valid_robots_txt') automatedValue = automatedResults.validRobotsTxt;
                        if (item.id === 'sitemap_xml_availability') automatedValue = automatedResults.sitemapXmlAvailable;
                        if (item.id === 'image_alt_attributes') automatedValue = automatedResults.imagesHaveAlt;
                    } else if (section.id === 'technical') {
                        if (item.id === 'mobile_responsiveness') automatedValue = automatedResults.hasViewportMeta;
                        if (item.id === 'html_lang_attribute') automatedValue = automatedResults.htmlLangAttribute;
                        if (item.id === 'https_usage') automatedValue = automatedResults.isHttps;
                        if (item.id === 'basic_redirect_check') automatedValue = automatedResults.redirectsProperly;
                        if (item.id === 'ssl_certificate_validity') automatedValue = automatedResults.sslCertificateValid;
                        if (item.id === 'website_analytics_detection') automatedValue = automatedResults.googleAnalyticsPresent;
                    } else if (section.id === 'functionality_basics') {
                        if (item.id === 'feedback_form') automatedValue = automatedResults.feedbackFormPresent;
                        if (item.id === 'back_to_top') automatedValue = automatedResults.backToTopButton;
                        if (item.id === 'simple_url') automatedValue = automatedResults.simpleURL;
                        if (item.id === 'links_to_homepage') automatedValue = automatedResults.linksToHomePage;
                        if (item.id === 'contact_info') automatedValue = automatedResults.contactInfoPresent;
                        if (item.id === 'searching_facility') automatedValue = automatedResults.searchingFacilityPresent;
                        if (item.id === 'privacy_policy') automatedValue = automatedResults.privacyPolicyLink;
                        if (item.id === 'cookie_policy') automatedValue = automatedResults.cookiePolicyLink;
                        if (item.id === 'site_map') automatedValue = automatedResults.siteMapLink;
                    } else if (section.id === 'security') {
                        if (item.id === 'security_headers_csp') automatedValue = automatedResults.securityHeaders?.hasCSP;
                        if (item.id === 'security_headers_xfo') automatedValue = automatedResults.securityHeaders?.hasXFO;
                        if (item.id === 'security_headers_hsts') automatedValue = automatedResults.securityHeaders?.hasHSTS;
                    }
                }

                return (
                    <AuditItem
                        key={item.id}
                        item={item}
                        automatedValue={automatedValue}
                        theme={theme}
                    />
                );
            })}
        </div>
    );
};


// Initial UI/UX audit criteria for automated audit (no manual items)
const initialAuditCriteria = [
    {
        id: 'performance_desktop',
        title: 'Performance (Desktop)',
        icon: Zap,
        items: [
            { id: 'fcp_d', question: 'First Contentful Paint', metricKey: 'FCP' },
            { id: 'lcp_d', question: 'Largest Contentful Paint', metricKey: 'LCP' },
            { id: 'tbt_d', question: 'Total Blocking Time', metricKey: 'TBT' },
            { id: 'cls_d', question: 'Cumulative Layout Shift', metricKey: 'CLS' },
            { id: 'si_d', question: 'Speed Index', metricKey: 'SpeedIndex' },
        ],
    },
    {
        id: 'performance_mobile',
        title: 'Performance (Mobile)',
        icon: Zap,
        items: [
            { id: 'fcp_m', question: 'First Contentful Paint', metricKey: 'FCP' },
            { id: 'lcp_m', question: 'Largest Contentful Paint', metricKey: 'LCP' },
            { id: 'tbt_m', question: 'Total Blocking Time', metricKey: 'TBT' },
            { id: 'cls_m', question: 'Cumulative Layout Shift', metricKey: 'CLS' },
            { id: 'si_m', question: 'Speed Index', metricKey: 'SpeedIndex' },
        ],
    },
    {
        id: 'seo',
        title: 'SEO Essentials',
        icon: Search,
        items: [
            { id: 'meta_description', question: 'Meta description presence' },
            { id: 'title_tag_presence', question: 'Title tag presence' },
            { id: 'robots_txt_availability', question: 'Robots.txt availability' },
            { id: 'sitemap_xml_availability', question: 'Sitemap.xml availability' },
            { id: 'image_alt_attributes', question: 'Alt text for images' },
            { id: 'crawlable_links', question: 'Crawlable Links' },
        ],
    },
    {
        id: 'technical',
        title: 'Technical (Core)',
        icon: FileText,
        items: [
            { id: 'mobile_responsiveness', question: 'Mobile responsiveness (viewport tag)' },
            { id: 'html_lang_attribute', question: 'HTML <lang> attribute check' },
            { id: 'https_usage', question: 'HTTPS usage' },
            { id: 'basic_redirect_check', question: 'Basic redirect check (e.g., non-www → www)' },
            { id: 'ssl_certificate_validity', question: 'SSL certificate validity check' },
            { id: 'website_analytics_detection', question: 'Website analytics detection (Google Analytics presence)' },
        ],
    },
    {
        id: 'functionality_basics',
        title: 'Functionality Basics',
        icon: Sliders,
        items: [
            { id: 'feedback_form', question: 'Feedback/Contact form presence' },
            { id: 'back_to_top', question: '"Back to Top" button' },
            { id: 'simple_url', question: 'Simple URL structure' },
            { id: 'links_to_homepage', question: 'Internal homepage link' },
            { id: 'contact_info', question: 'Contact information present' },
            { id: 'searching_facility', question: 'Searching facility present' },
            { id: 'privacy_policy', question: 'Privacy Policy link present' },
            { id: 'cookie_policy', question: 'Cookie Policy link present' },
            { id: 'site_map', question: 'Site Map link present' },
        ],
    },
    {
        id: 'security',
        title: 'Security',
        icon: ShieldCheck,
        items: [
            { id: 'security_headers_csp', question: 'Content Security Policy' },
            { id: 'security_headers_xfo', question: 'X-Frame-Options' },
            { id: 'security_headers_hsts', question: 'Strict Transport Security' },
        ],
    },
];


// Main App Component
const App = () => {
    const [websiteUrl, setWebsiteUrl] = useState('');
    const cloudflareWorkerUrl = 'https://qa-agent.sanda.workers.dev/';
    const [automatedReport, setAutomatedReport] = useState(null);
    const [isLoadingAutomated, setIsLoadingAutomated] = useState(false);
    const [automatedError, setAutomatedError] = useState('');
    const [theme, setTheme] = useState('light');

    const auditCategories = initialAuditCriteria;

    useEffect(() => {
        if (theme === 'dark') {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [theme]);

    const runAutomatedAudit = async () => {
        if (!websiteUrl) {
            setAutomatedError('Please enter a website URL to audit.');
            return;
        }
        if (cloudflareWorkerUrl === 'YOUR_CLOUDFLARE_WORKER_URL_HERE' || !cloudflareWorkerUrl.startsWith('https://')) {
            setAutomatedError('The Cloudflare Worker URL has not been correctly configured. Please update the `cloudflareWorkerUrl` variable in the code with your deployed worker URL.');
            return;
        }

        setAutomatedReport(null);
        setAutomatedError('');
        setIsLoadingAutomated(true);

        try {
            const response = await fetch(cloudflareWorkerUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ url: websiteUrl }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to get automated report');
            }

            setAutomatedReport(data);

        } catch (error) {
            console.error('Automated audit failed:', error);
            setAutomatedError(`Automated audit failed: ${error.message}. Please ensure your Cloudflare Worker is correctly deployed and configured, and the URL in the code is correct.`);
        } finally {
            setIsLoadingAutomated(false);
        }
    };

    const resetAudit = () => {
        setWebsiteUrl('');
        setAutomatedReport(null);
        setAutomatedError('');
        setIsLoadingAutomated(false);
    };

    // Updated toggleTheme function
    const toggleTheme = (e) => {
        setTheme(e.target.checked ? 'dark' : 'light');
    };

    // Automated Report Summary Display
    const renderAutomatedReportSummary = () => {
        if (!automatedReport) return null;

        const getStatusIndicator = (value) => {
            if (typeof value === 'boolean') {
                return value ? <span className="text-green-600 dark:text-green-400 font-semibold">Yes</span> : <span className="text-red-600 dark:text-red-400 font-semibold">No</span>;
            }
            return value;
        };

        return (
            <div className={`p-6 rounded-xl shadow-lg mb-8 border ${theme === 'dark' ? 'bg-gray-800 text-gray-100 border-gray-700' : 'bg-white text-gray-900 border-gray-200'}`}>
                <h2 className={`text-2xl font-bold mb-6 flex items-center ${theme === 'dark' ? 'text-blue-400' : C3S_COLOR_DARK_BLUE}`}>
                    <Cloud className={`w-7 h-7 mr-3 ${theme === 'dark' ? 'text-blue-400' : C3S_COLOR_LIGHT_BLUE}`} />
                    Automated Audit Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><span className="font-semibold">URL Audited:</span> <a href={automatedReport.finalUrl || automatedReport.requestedUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline dark:text-blue-400">{automatedReport.finalUrl || automatedReport.requestedUrl}</a></div>
                    <div><span className="font-semibold">Fetched At:</span> {new Date(automatedReport.fetchedAt).toLocaleString()}</div>
                    <div><span className="font-semibold">Page Title:</span> {automatedReport.pageTitle}</div>
                    <div><span className="font-semibold">Meta Description:</span> {automatedReport.metaDescription}</div>
                    <div><span className="font-semibold">Has Viewport Meta:</span> {getStatusIndicator(automatedReport.hasViewportMeta)}</div>
                    <div><span className="font-semibold">Viewport Content:</span> {automatedReport.viewportContent}</div>
                    <div><span className="font-semibold">HTML Lang Attribute:</span> {getStatusIndicator(automatedReport.htmlLangAttribute)}</div>
                    {automatedReport.securityHeaders && (
                        <>
                            <div><span className="font-semibold">CSP Header:</span> {getStatusIndicator(automatedReport.securityHeaders.hasCSP)}</div>
                            <div><span className="font-semibold">X-Frame-Options Header:</span> {getStatusIndicator(automatedReport.securityHeaders.hasXFO)}</div>
                            <div><span className="font-semibold">HSTS Header:</span> {getStatusIndicator(automatedReport.securityHeaders.hasHSTS)}</div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`min-h-screen font-sans p-4 sm:p-6 lg:p-8 flex flex-col items-center py-10 transition-colors duration-300 ${theme === 'dark' ? 'dark-canvas-background' : 'canvas-background'}`}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
                    body {
                        font-family: 'Inter', sans-serif;
                        margin: 0;
                        padding: 0;
                    }
                    .canvas-background {
                        background-color: #ffffff;
                        background-image: radial-gradient(rgba(12, 12, 12, 0.191) 1px, transparent 0);
                        background-size: 11px 11px;
                        background-position: -5px -5px;
                    }
                    .dark-canvas-background {
                        background-color: #1a202c;
                        background-image: radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 0);
                        background-size: 11px 11px;
                        background-position: -5px -5px;
                    }

                    /* --- New Toggle Button Styles --- */
                    .switch {
                        position: relative;
                        display: inline-block;
                        width: 80px; /* Adjusted width */
                        height: 35px; /* Adjusted height */
                    }
                    
                    .switch #input {
                        opacity: 0;
                        width: 0;
                        height: 0;
                    }
                    
                    .slider {
                        position: absolute;
                        cursor: pointer;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: #2196f3; /* Light mode background */
                        -webkit-transition: 0.4s;
                        transition: 0.4s;
                        z-index: 0;
                        overflow: hidden;
                        border-radius: 34px; /* Rounded corners */
                    }
                    
                    .sun-moon {
                        position: absolute;
                        content: "";
                        height: 27px; /* Adjusted size for the 'ball' */
                        width: 27px; /* Adjusted size for the 'ball' */
                        left: 4px; /* Initial position for light mode */
                        bottom: 4px;
                        background-color: yellow; /* Sun color */
                        -webkit-transition: 0.4s;
                        transition: 0.4s;
                        border-radius: 50%; /* Make it round */
                        z-index: 2; /* Ensure sun/moon is above background but below stars */
                    }

                    /* Dark mode checked state for slider and sun-moon */
                    #input:checked + .slider {
                        background-color: black;
                        /* Added box-shadow for a 3D effect and contrast in dark mode */
                        box-shadow: 0 0 10px rgba(128, 128, 128, 0.5), inset 0 0 10px rgba(255, 255, 255, 0.2);
                    }
                    
                    #input:checked + .slider .sun-moon {
                        -webkit-transform: translateX(45px); /* Move to the right for dark mode */
                        -ms-transform: translateX(45px);
                        transform: translateX(45px); /* Adjusted translation based on new width/height */
                        background-color: white; /* Moon color */
                        -webkit-animation: rotate-center 0.6s ease-in-out both;
                        animation: rotate-center 0.6s ease-in-out both;
                    }
                    
                    /* Moon dots */
                    .moon-dot {
                        opacity: 0;
                        transition: 0.4s;
                        fill: gray;
                    }
                    
                    #input:checked + .slider .sun-moon .moon-dot {
                        opacity: 1;
                    }
                    
                    /* Moon dot positions relative to sun-moon (27x27px) */
                    #moon-dot-1 {
                        left: 5px; /* Adjusted for 27x27 */
                        top: 3px;
                        position: absolute;
                        width: 6px;
                        height: 6px;
                        z-index: 4;
                    }
                    
                    #moon-dot-2 {
                        left: 2px;
                        top: 10px;
                        position: absolute;
                        width: 10px;
                        height: 10px;
                        z-index: 4;
                    }
                    
                    #moon-dot-3 {
                        left: 14px; /* Adjusted for 27x27 */
                        top: 17px; /* Adjusted for 27x27 */
                        position: absolute;
                        width: 3px;
                        height: 3px;
                        z-index: 4;
                    }
                    
                    /* Light rays (sun effect) */
                    .light-ray {
                        fill: white;
                        opacity: 10%;
                        position: absolute;
                        z-index: -1;
                    }
                    
                    #light-ray-1 {
                        left: -8px;
                        top: -8px;
                        width: 43px;
                        height: 43px;
                    }
                    
                    #light-ray-2 {
                        left: -50%;
                        top: -50%;
                        width: 55px;
                        height: 55px;
                    }
                    
                    #light-ray-3 {
                        left: -18px;
                        top: -18px;
                        width: 60px;
                        height: 60px;
                    }
                    
                    /* Clouds (light mode) */
                    .cloud-light {
                        position: absolute;
                        fill: #eee;
                        animation-name: cloud-move;
                        animation-duration: 6s;
                        animation-iteration-count: infinite;
                        opacity: 1; /* Visible in light mode */
                        transition: opacity 0.4s;
                    }
                    
                    .cloud-dark {
                        position: absolute;
                        fill: #ccc;
                        animation-name: cloud-move;
                        animation-duration: 6s;
                        animation-iteration-count: infinite;
                        animation-delay: 1s;
                        opacity: 0; /* Hidden in light mode */
                        transition: opacity 0.4s;
                    }
                    
                    /* Cloud positions (relative to slider) */
                    #cloud-1 { left: 35px; top: 8px; width: 25px; height: 15px; border-radius: 50%; }
                    #cloud-2 { left: 48px; top: 5px; width: 15px; height: 10px; border-radius: 50%; }
                    #cloud-3 { left: 30px; top: 18px; width: 20px; height: 12px; border-radius: 50%; }
                    #cloud-4 { left: 40px; top: 10px; width: 25px; height: 15px; border-radius: 50%; } /* Light cloud visible in light mode */
                    #cloud-5 { left: 52px; top: 7px; width: 15px; height: 10px; border-radius: 50%; } /* Light cloud visible in light mode */
                    #cloud-6 { left: 34px; top: 20px; width: 20px; height: 12px; border-radius: 50%; } /* Light cloud visible in light mode */
                    
                    /* Stars (dark mode) */
                    .stars {
                        position: absolute;
                        width: 100%; /* Cover the slider width */
                        height: 100%; /* Cover the slider height */
                        top: 0;
                        left: 0;
                        transform: translateY(-100%); /* Start above, hidden */
                        opacity: 0;
                        transition: opacity 0.4s, transform 0.4s;
                        z-index: 3; /* Ensure stars are above the sun-moon */
                    }
                    
                    .star {
                        position: absolute;
                        background-color: white;
                        border-radius: 50%;
                        filter: drop-shadow(0 0 2px rgba(255,255,255,0.7));
                    }
                    /* Repositioned stars to appear on the left side when moon moves right */
                    #star-1 { width: 3px; height: 3px; top: 8px; left: 10px; }
                    #star-2 { width: 2.5px; height: 2.5px; top: 18px; left: 25px; }
                    #star-3 { width: 4px; height: 4px; top: 25px; left: 15px; }
                    #star-4 { width: 3px; height: 3px; top: 12px; left: 30px; }
                    
                    /* Animations */
                    @keyframes rotate-center {
                        0% {
                            -webkit-transform: translateX(0);
                            transform: translateX(0);
                        }
                        100% {
                            -webkit-transform: translateX(45px) rotate(360deg); /* Rotate for moon */
                            transform: translateX(45px) rotate(360deg);
                        }
                    }

                    @keyframes cloud-move {
                        0% { transform: translateX(0px); }
                        40% { transform: translateX(4px); }
                        80% { transform: translateX(-4px); }
                        100% { transform: translateX(0px); }
                    }
                    
                    @keyframes star-twinkle {
                        0% { transform: scale(1); }
                        40% { transform: scale(1.2); }
                        80% { transform: scale(0.8); }
                        100% { transform: scale(1); }
                    }

                    /* Dark mode checked states for clouds and stars */
                    #input:checked + .slider .stars {
                        -webkit-transform: translateY(0);
                        -ms-transform: translateY(0);
                        transform: translateY(0);
                        opacity: 1;
                    }

                    #input:checked + .slider .cloud-light {
                        opacity: 0; /* Hide light clouds in dark mode */
                    }
                    
                    #input:checked + .slider .cloud-dark {
                        opacity: 1; /* Show dark clouds in dark mode */
                    }

                    /* General styling adjustments for dark mode */
                    .dark-mode .text-gray-900 { color: #f8f8f8; }
                    .dark-mode .text-gray-800 { color: #e0e0e0; }
                    .dark-mode .text-gray-700 { color: #d0d0d0; }
                    .dark-mode .bg-white { background-color: #1f2937; }
                    .dark-mode .border-gray-200 { border-color: #4b5563; }
                    .dark-mode .bg-gradient-to-br { background: #1f2937; }
                    .dark-mode .text-blue-500 { color: #93c5fd; }
                    .dark-mode .hover\\:underline { text-decoration-color: #93c5fd; }
                    .dark-mode .focus\\:ring-blue-500 { --tw-ring-color: #60a5fa; }

                    /* Custom Tailwind-like colors for 3CS logo */
                    .text-c3s-dark-blue { color: ${C3S_COLOR_DARK_BLUE}; }
                    .text-c3s-light-blue { color: ${C3S_COLOR_LIGHT_BLUE}; }
                    .bg-c3s-dark-blue { background-color: ${C3S_COLOR_DARK_BLUE}; }
                    .hover\\:bg-c3s-light-blue:hover { background-color: ${C3S_COLOR_LIGHT_BLUE}; }

                    .dark-mode .text-c3s-dark-blue { color: ${C3S_COLOR_LIGHT_BLUE}; }
                    .dark-mode .text-c3s-light-blue { color: #93c5fd; }
                    .dark-mode .bg-c3s-dark-blue { background-color: #3e8fb0; }
                    .dark-mode .hover\\:bg-c3s-light-blue:hover { background-color: #3182CE; }

                    /* Loading spinner */
                    .loading-spinner {
                        border: 4px solid rgba(255, 255, 255, 0.3);
                        border-top-color: currentColor;
                        border-radius: 50%;
                        width: 1.5em;
                        height: 1.5em;
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div className="w-full max-w-4xl">
                {/* Logo and Dark Mode Toggle in a flex container */}
                <div className="flex justify-between items-center mb-8">
                    {/* Logo - Retained as per policy on watermarks/branding */}
                    <img
                        src={process.env.PUBLIC_URL + '/3CS.webp'}
                        alt="3CS Logo"
                        className="h-14 w-auto"
                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/100x40/1a5585/ffffff?text=3CS"; }}
                    />
                    {/* New Toggle Button HTML from toggle.txt */}
                    <label className="switch">
                      <input id="input" type="checkbox" checked={theme === 'dark'} onChange={toggleTheme} />
                      <div className="slider round">
                        <div className="sun-moon">
                          <svg id="moon-dot-1" className="moon-dot" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="moon-dot-2" className="moon-dot" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="moon-dot-3" className="moon-dot" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="light-ray-1" className="light-ray" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="light-ray-2" className="light-ray" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="light-ray-3" className="light-ray" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="cloud-1" className="cloud-dark" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="cloud-2" className="cloud-dark" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="cloud-3" className="cloud-dark" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="cloud-4" className="cloud-light" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="cloud-5" className="cloud-light" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                          <svg id="cloud-6" className="cloud-light" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="50"></circle>
                          </svg>
                        </div>
                        <div className="stars">
                          <svg id="star-1" className="star" viewBox="0 0 20 20">
                            <path d="M 0 10 C 10 10,10 10 ,0 10 C 10 10 , 10 10 , 10 20 C 10 10 , 10 10 , 20 10 C 10 10 , 10 10 , 10 0 C 10 10,10 10 ,0 10 Z"></path>
                          </svg>
                          <svg id="star-2" className="star" viewBox="0 0 20 20">
                            <path d="M 0 10 C 10 10,10 10 ,0 10 C 10 10 , 10 10 , 10 20 C 10 10 , 10 10 , 20 10 C 10 10 , 10 10 , 10 0 C 10 10,10 10 ,0 10 Z"></path>
                          </svg>
                          <svg id="star-3" className="star" viewBox="0 0 20 20">
                            <path d="M 0 10 C 10 10,10 10 ,0 10 C 10 10 , 10 10 , 10 20 C 10 10 , 10 10 , 20 10 C 10 10 , 10 10 , 10 0 C 10 10,10 10 ,0 10 Z"></path>
                          </svg>
                          <svg id="star-4" className="star" viewBox="0 0 20 20">
                            <path d="M 0 10 C 10 10,10 10 ,0 10 C 10 10 , 10 10 , 10 20 C 10 10 , 10 10 , 20 10 C 10 10 , 10 10 , 10 0 C 10 10,10 10 ,0 10 Z"></path>
                          </svg>
                        </div>
                      </div>
                    </label>
                </div>

                {/* Main Title - Updated */}
                <h1 className={`text-4xl font-extrabold text-center mb-8 drop-shadow-sm ${theme === 'dark' ? 'text-c3s-light-blue' : 'text-c3s-dark-blue'}`}>
                    Nautilus (AI Web Audit)
                </h1>

                {/* URL Input Section */}
                <div className={`p-6 rounded-xl shadow-lg mb-8 border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <label htmlFor="website-url" className={`block text-xl font-semibold mb-3 ${theme === 'dark' ? 'text-gray-100' : 'text-c3s-dark-blue'}`}>
                        Website URL to Audit:
                    </label>
                    <input
                        type="url"
                        id="website-url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="e.g., https://www.example.com"
                        className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 text-lg shadow-sm ${
                            theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500' : 'bg-white border-gray-300 text-gray-800 focus:ring-blue-500'
                        } mb-4`}
                    />

                    <button
                        onClick={runAutomatedAudit}
                        disabled={isLoadingAutomated}
                        className={`w-full mt-6 py-3 px-6 rounded-lg font-bold shadow-md transition duration-300 ease-in-out transform ${
                            isLoadingAutomated
                                ? 'bg-blue-300 cursor-not-allowed dark:bg-blue-500 dark:text-gray-300'
                                : `bg-c3s-dark-blue hover:bg-c3s-light-blue text-white hover:scale-105`
                        } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center`}
                    >
                        {isLoadingAutomated && <div className="loading-spinner mr-3"></div>}
                        {isLoadingAutomated ? 'Running Audit...' : 'Run Automated Audit'}
                    </button>
                    {automatedError && (
                        <p className={`text-red-600 text-sm mt-3 text-center ${theme === 'dark' ? 'text-red-400' : ''}`}>{automatedError}</p>
                    )}
                </div>

                {/* Automated Audit Report Display */}
                {automatedReport && (
                    <div className="w-full">
                        {renderAutomatedReportSummary()}

                        {auditCategories.map((section) => (
                            <AuditSection
                                key={section.id}
                                section={section}
                                automatedResults={automatedReport}
                                theme={theme}
                            />
                        ))}

                        <div className="text-center mt-8">
                            <button
                                onClick={resetAudit}
                                className={`bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 ${theme === 'dark' ? 'dark:bg-gray-600 dark:hover:bg-gray-700' : ''}`}
                            >
                                Run New Audit
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <footer className={`mt-10 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    <p>All rights reserved 2025 | Powered by 3CS</p>
                </footer>
            </div>
        </div>
    );
};

export default App;